// useProjectTaskViewState.js
// Persists and live-syncs sort/filter/viewMode for ProjectTasksPage via
// Supabase app_settings.
//
// Two storage keys per project:
//   Shared  : "project_tasks_view_<projectId>"            — visible to everyone
//   Personal: "project_tasks_view_personal_<uid>_<pid>"   — current user only
//
// Personal key takes priority over shared on load.
//
// Save routing
// ────────────
// • Every change (sort/filter/viewMode) is debounced and auto-saved to the
//   currently-active key (personal if override is active, shared otherwise).
//   This means rules always persist across refresh without any explicit action.
//
// • "Save for everyone" → immediately writes to shared key and clears the
//   personal override, so subsequent auto-saves also go to shared.
//
// • "Save for me" → immediately writes to personal key and activates the
//   personal override, so subsequent auto-saves go to personal only.
//
// Reliability guarantees
// ──────────────────────
// • Normal saves    : debounced 300 ms after the last change.
// • In-app nav      : pending save flushed immediately on hook unmount.
// • Browser refresh : pending save sent via fetch({ keepalive: true }) in
//                     a beforeunload handler so the browser keeps the
//                     request alive even as the page tears down.
// • Cross-user sync : postgres_changes realtime subscription propagates
//                     another user's shared-key save within ~1 s (ignored
//                     when a personal override is active for the current user).

import { useState, useEffect, useCallback, useRef } from 'react';
import { supabase, SUPABASE_URL, SUPABASE_ANON_KEY } from '../supabaseClient';

const DEBOUNCE_MS = 300;
const KEY_PREFIX = 'project_tasks_view_';

function sharedKey(projectId) {
  return KEY_PREFIX + String(projectId);
}

function personalKey(projectId, userId) {
  return KEY_PREFIX + 'personal_' + String(userId) + '_' + String(projectId);
}

function parseValue(raw) {
  const v = (raw && typeof raw === 'object') ? raw : {};
  return {
    sortRules: Array.isArray(v.sortRules) ? v.sortRules : [],
    filterRules: Array.isArray(v.filterRules) ? v.filterRules : [],
    viewMode: v.viewMode === 'board' ? 'board' : 'table',
  };
}

// Fire-and-forget upsert using plain fetch with keepalive so it survives
// page unloads (browser refresh). Called from beforeunload only.
function beaconSave(key, value, accessToken) {
  if (!SUPABASE_URL || !key) return;
  const url = SUPABASE_URL + '/rest/v1/app_settings';
  const body = JSON.stringify({ key, value, updated_at: new Date().toISOString() });
  const headers = {
    'Content-Type': 'application/json',
    'apikey': SUPABASE_ANON_KEY || '',
    'Authorization': accessToken ? 'Bearer ' + accessToken : 'Bearer ' + (SUPABASE_ANON_KEY || ''),
    'Prefer': 'resolution=merge-duplicates',
  };
  try {
    fetch(url, { method: 'POST', headers, body, keepalive: true });
  } catch (_) { /* non-fatal */ }
}

export function useProjectTaskViewState(projectId, userId) {
  const [sortRules, setSortRulesState] = useState([]);
  const [filterRules, setFilterRulesState] = useState([]);
  const [viewMode, setViewModeState] = useState('table');
  const [loading, setLoading] = useState(true);
  const [hasPersonalOverride, setHasPersonalOverride] = useState(false);

  // Always-current refs — safe to read inside timers and event handlers
  const latestRef = useRef({ sortRules: [], filterRules: [], viewMode: 'table' });
  const projectIdRef = useRef(projectId);
  const userIdRef = useRef(userId);
  const accessTokenRef = useRef(null);
  const saveTimerRef = useRef(null);
  const hasPendingSaveRef = useRef(false); // true when latestRef differs from last DB write
  const isFetchedRef = useRef(false);      // guards against saves before initial fetch
  const hasPersonalRef = useRef(false);    // mirrors hasPersonalOverride for use in closures

  useEffect(() => { projectIdRef.current = projectId; }, [projectId]);
  useEffect(() => { userIdRef.current = userId; }, [userId]);

  // Cache the access token for use in beforeunload (sync context)
  useEffect(() => {
    supabase.auth.getSession().then(({ data }) => {
      accessTokenRef.current = data?.session?.access_token || null;
    });
    const { data: { subscription } } = supabase.auth.onAuthStateChange((_e, session) => {
      accessTokenRef.current = session?.access_token || null;
    });
    return () => subscription.unsubscribe();
  }, []);

  // ── Core upsert to a specific key ─────────────────────────────────────
  const doSaveToKey = useCallback(async (key, value) => {
    const { error } = await supabase
      .from('app_settings')
      .upsert(
        { key, value, updated_at: new Date().toISOString() },
        { onConflict: 'key' }
      );
    if (error) console.warn('[useProjectTaskViewState] save error:', error);
    return !error;
  }, []);

  // ── Resolve the active key (personal if override is set, shared otherwise) ──
  const activeKey = useCallback((pid, uid) => {
    return (hasPersonalRef.current && uid) ? personalKey(pid, uid) : sharedKey(pid);
  }, []);

  // ── Schedule debounced save to the active key ──────────────────────────
  const scheduleSave = useCallback((next) => {
    if (!isFetchedRef.current) return;
    latestRef.current = next;
    hasPendingSaveRef.current = true;
    if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
    saveTimerRef.current = setTimeout(() => {
      const pid = projectIdRef.current;
      const uid = userIdRef.current;
      if (!pid) return;
      const key = activeKey(pid, uid);
      doSaveToKey(key, latestRef.current).then((ok) => {
        if (ok) hasPendingSaveRef.current = false;
      });
    }, DEBOUNCE_MS);
  }, [doSaveToKey, activeKey]);

  // ── Flush any pending save immediately (for unmount / project change) ──
  const flushSave = useCallback(() => {
    if (!hasPendingSaveRef.current) return;
    if (saveTimerRef.current) { clearTimeout(saveTimerRef.current); saveTimerRef.current = null; }
    const pid = projectIdRef.current;
    const uid = userIdRef.current;
    if (pid) doSaveToKey(activeKey(pid, uid), latestRef.current);
  }, [doSaveToKey, activeKey]);

  // ── Setters — apply immediately and auto-save to active key ───────────
  const setSortRules = useCallback((next) => {
    const resolved = typeof next === 'function' ? next(latestRef.current.sortRules) : next;
    setSortRulesState(resolved);
    scheduleSave({ ...latestRef.current, sortRules: resolved });
  }, [scheduleSave]);

  const setFilterRules = useCallback((next) => {
    const resolved = typeof next === 'function' ? next(latestRef.current.filterRules) : next;
    setFilterRulesState(resolved);
    scheduleSave({ ...latestRef.current, filterRules: resolved });
  }, [scheduleSave]);

  const setViewMode = useCallback((next) => {
    const resolved = typeof next === 'function' ? next(latestRef.current.viewMode) : next;
    setViewModeState(resolved);
    scheduleSave({ ...latestRef.current, viewMode: resolved });
  }, [scheduleSave]);

  // ── Explicit save: current state → shared key (clears personal override) ──
  // When clearAllPersonal=true (admin/supervisor), also deletes every other
  // user's personal override for this project so the shared state is authoritative.
  const saveForEveryone = useCallback(async ({ clearAllPersonal = false } = {}) => {
    if (saveTimerRef.current) { clearTimeout(saveTimerRef.current); saveTimerRef.current = null; }
    hasPendingSaveRef.current = false;
    hasPersonalRef.current = false;
    setHasPersonalOverride(false);
    const pid = projectIdRef.current;
    const uid = userIdRef.current;
    if (!pid) return;
    await doSaveToKey(sharedKey(pid), latestRef.current);
    if (clearAllPersonal) {
      // Delete all personal overrides for this project (admin/supervisor override)
      await supabase
        .from('app_settings')
        .delete()
        .like('key', KEY_PREFIX + 'personal_%_' + String(pid));
    } else if (uid) {
      // Only remove the current user's personal override
      await supabase.from('app_settings').delete().eq('key', personalKey(pid, uid));
    }
  }, [doSaveToKey]);

  // ── Explicit save: current state → personal key (activates override) ──
  const saveForMe = useCallback(async () => {
    if (saveTimerRef.current) { clearTimeout(saveTimerRef.current); saveTimerRef.current = null; }
    hasPendingSaveRef.current = false;
    const pid = projectIdRef.current;
    const uid = userIdRef.current;
    if (!pid || !uid) return;
    hasPersonalRef.current = true;
    setHasPersonalOverride(true);
    await doSaveToKey(personalKey(pid, uid), latestRef.current);
  }, [doSaveToKey]);

  // ── Initial fetch: personal key first, fall back to shared ────────────
  useEffect(() => {
    if (!projectId) { setLoading(false); isFetchedRef.current = true; return; }

    // Flush any pending save from the previous project
    flushSave();

    setLoading(true);
    isFetchedRef.current = false;
    hasPendingSaveRef.current = false;
    hasPersonalRef.current = false;

    let cancelled = false;

    const fetchData = async () => {
      // 1. Try personal key (requires userId)
      if (userId) {
        const { data: pData } = await supabase
          .from('app_settings')
          .select('value')
          .eq('key', personalKey(projectId, userId))
          .maybeSingle();

        if (cancelled) return;

        if (pData?.value) {
          const next = parseValue(pData.value);
          setSortRulesState(next.sortRules);
          setFilterRulesState(next.filterRules);
          setViewModeState(next.viewMode);
          latestRef.current = next;
          hasPersonalRef.current = true;
          setHasPersonalOverride(true);
          isFetchedRef.current = true;
          setLoading(false);
          return;
        }
      }

      if (cancelled) return;

      // 2. Fall back to shared key
      const { data, error } = await supabase
        .from('app_settings')
        .select('value')
        .eq('key', sharedKey(projectId))
        .maybeSingle();

      if (cancelled) return;
      if (error) console.warn('[useProjectTaskViewState] fetch error:', error);

      const next = parseValue(data?.value);
      setSortRulesState(next.sortRules);
      setFilterRulesState(next.filterRules);
      setViewModeState(next.viewMode);
      latestRef.current = next;
      hasPersonalRef.current = false;
      setHasPersonalOverride(false);
      isFetchedRef.current = true;
      setLoading(false);
    };

    fetchData();
    return () => { cancelled = true; };
  }, [projectId, userId]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Realtime: shared key (ignored when personal override is active) ───
  useEffect(() => {
    if (!projectId) return;
    const key = sharedKey(projectId);

    const channel = supabase
      .channel('project-task-view-shared-' + projectId)
      .on(
        'postgres_changes',
        { event: '*', schema: 'public', table: 'app_settings', filter: 'key=eq.' + key },
        (payload) => {
          if (hasPersonalRef.current) return; // personal override takes precedence
          const next = parseValue(payload.new?.value);
          const cur = latestRef.current;
          const sortChanged = JSON.stringify(cur.sortRules) !== JSON.stringify(next.sortRules);
          const filterChanged = JSON.stringify(cur.filterRules) !== JSON.stringify(next.filterRules);
          const viewChanged = cur.viewMode !== next.viewMode;
          if (sortChanged) setSortRulesState(next.sortRules);
          if (filterChanged) setFilterRulesState(next.filterRules);
          if (viewChanged) setViewModeState(next.viewMode);
          if (sortChanged || filterChanged || viewChanged) latestRef.current = next;
        }
      )
      .subscribe();

    return () => { supabase.removeChannel(channel); };
  }, [projectId]);

  // ── Realtime: personal key (multi-tab sync for personal overrides) ────
  useEffect(() => {
    if (!projectId || !userId) return;
    const key = personalKey(projectId, userId);

    const channel = supabase
      .channel('project-task-view-personal-' + projectId + '-' + userId)
      .on(
        'postgres_changes',
        { event: '*', schema: 'public', table: 'app_settings', filter: 'key=eq.' + key },
        (payload) => {
          if (!payload.new?.value) return; // row deleted → ignore
          const next = parseValue(payload.new.value);
          const cur = latestRef.current;
          const sortChanged = JSON.stringify(cur.sortRules) !== JSON.stringify(next.sortRules);
          const filterChanged = JSON.stringify(cur.filterRules) !== JSON.stringify(next.filterRules);
          const viewChanged = cur.viewMode !== next.viewMode;
          if (sortChanged) setSortRulesState(next.sortRules);
          if (filterChanged) setFilterRulesState(next.filterRules);
          if (viewChanged) setViewModeState(next.viewMode);
          if (sortChanged || filterChanged || viewChanged) {
            latestRef.current = next;
            hasPersonalRef.current = true;
          }
        }
      )
      .subscribe();

    return () => { supabase.removeChannel(channel); };
  }, [projectId, userId]);

  // ── Flush on unmount (in-app navigation) ──────────────────────────────
  useEffect(() => {
    return () => { flushSave(); };
  }, [flushSave]);

  // ── beforeunload: keepalive fetch survives browser refresh ─────────────
  useEffect(() => {
    const handleBeforeUnload = () => {
      if (!hasPendingSaveRef.current) return;
      if (saveTimerRef.current) { clearTimeout(saveTimerRef.current); saveTimerRef.current = null; }
      const pid = projectIdRef.current;
      const uid = userIdRef.current;
      const key = activeKey(pid, uid);
      beaconSave(key, latestRef.current, accessTokenRef.current);
    };
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [activeKey]);

  return {
    sortRules, setSortRules,
    filterRules, setFilterRules,
    viewMode, setViewMode,
    loading,
    saveForEveryone,
    saveForMe,
    hasPersonalOverride,
  };
}
